import { Modal } from "@bcc-ui/ui-components";
import ReactJsonView from "@microlink/react-json-view";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { ButtonContainer } from "components/button-container/ButtonContainer";
import Button from "components/button/Button";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import style from "components/workflows/launch-workflow-dialog/launch-workflow-dialog.scss";
import { WorkflowEntityType } from "domain/workflows";
import { templateService } from "services/workflows/TemplateService";
import { workflowService } from "services/workflows/WorkflowService";

import testIds from "testIds.json";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    workflowUuid: string;
    contextId: string;
    entityType: WorkflowEntityType;
}

export function JsonLogsDialog(props: Props): JSX.Element {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [jsonData, setJsonData] = React.useState<Record<string, unknown>>({});

    React.useEffect(() => {
        if (!props.isOpen || !props.contextId) {
            return;
        }
        const abortController = new AbortController();
        setLoading(true);
        const serviceCall =
            props.entityType === WorkflowEntityType.TEMPLATE
                ? templateService.fetchTemplateExecutionLogsContent(
                      props.workflowUuid,
                      props.contextId,
                      abortController
                  )
                : workflowService.fetchExecutionLogsContent(props.workflowUuid, props.contextId, abortController);

        serviceCall.then((result) => setJsonData(result as Record<string, unknown>)).finally(() => setLoading(false));

        return () => abortController.abort();
    }, [props.isOpen, props.contextId]);

    const handleExport = () => {
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = `${props.contextId}.json`;
        downloadLink.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Modal
            isOpen={props.isOpen}
            hideModal={props.onClose}
            title={t("WorkflowsTable.sidePanel.tabs.log.viewLogs")}
            testId={testIds.workArea.workflows.sidePanel.jsonLogsDialog.itself}
            width="Lg"
        >
            {loading ? (
                <LoadingIndicator />
            ) : (
                <div className={style.container}>
                    <div className={style.viewLogContainer}>
                        <ReactJsonView name={null} src={jsonData} displayDataTypes={false} enableClipboard={false} />
                    </div>
                    <ButtonContainer gap="SMALL">
                        <Button variant="PRIMARY" onClick={props.onClose} testId={testIds.common.dialog.closeButton}>
                            {t("Common.close")}
                        </Button>
                        <Button
                            variant="SECONDARY"
                            icon="EXPORT"
                            onClick={handleExport}
                            testId={testIds.workArea.workflows.sidePanel.jsonLogsDialog.exportButton}
                        >
                            {t("Common.exportAsJson")}
                        </Button>
                    </ButtonContainer>
                </div>
            )}
        </Modal>
    );
}
